// Pure simulated-gradient policy. No Bluetooth, no React, no timers.
//
// The gradient only exists while ERG is NOT driving the trainer. In that state
// it is the rider's resistance control, and it is also what the display shows
// in place of a watts target that would pilot nothing.

import { isErgActive, normalizeStepMode, STEP_MODE } from "./ergMode";

export const GRADIENT_MIN = 0;
export const GRADIENT_MAX = 20;
export const GRADIENT_STEP = 1;

// Starting gradient for each non-ERG mode, in percent.
// 0 for what should be easy; 2 for a sprint, so there is something to push
// against immediately instead of spinning out before drag has time to build.
export const GRADIENT_DEFAULTS = Object.freeze({
  free: 0,
  max: 2,
  manual: 0,
});

const clampNumber = (n) =>
  Math.min(GRADIENT_MAX, Math.max(GRADIENT_MIN, Math.round(n)));

// Negative is deliberately unreachable: a trainer is a brake, not a motor, so a
// downhill would only mean minimum resistance and an unpleasant spin-out.
//
// Type is checked before any coercion. Number("") and Number([]) are both 0, so
// a bare Number() would turn empty or garbage input into a real 0% gradient
// rather than a rejection — the same coercion trap that cost a review round on
// the previous branch.
export function clampGradient(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? clampNumber(value) : GRADIENT_MIN;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return GRADIENT_MIN;
    const n = Number(trimmed);
    return Number.isFinite(n) ? clampNumber(n) : GRADIENT_MIN;
  }
  return GRADIENT_MIN;
}

export function adjustGradient(current, delta) {
  const step = Number(delta);
  const safeStep = Number.isFinite(step) ? Math.round(step) : 0;
  return clampGradient(clampGradient(current) + safeStep);
}

// Which remembered gradient applies right now, or null when ERG is driving and
// there is therefore no gradient at all.
export function resolveGradientMode({ canErgControl, ergEnabled, stepMode } = {}) {
  if (isErgActive({ canErgControl, ergEnabled, stepMode })) return null;
  const mode = normalizeStepMode(stepMode);
  if (mode === STEP_MODE.FREE) return "free";
  if (mode === STEP_MODE.MAX) return "max";
  return "manual";
}

export function createGradientState() {
  return { ...GRADIENT_DEFAULTS };
}

export function getGradient(state, gradientMode) {
  if (!state || !gradientMode) return GRADIENT_MIN;
  if (!(gradientMode in GRADIENT_DEFAULTS)) return GRADIENT_MIN;
  return clampGradient(state[gradientMode] ?? GRADIENT_DEFAULTS[gradientMode]);
}

export function setGradient(state, gradientMode, value) {
  if (!state) return state;
  if (!gradientMode || !(gradientMode in GRADIENT_DEFAULTS)) return state;
  return { ...state, [gradientMode]: clampGradient(value) };
}
